import { useRef, useState } from 'react';
import { Trash, UploadSimple } from '@phosphor-icons/react';
import { ApiError, request } from './api';
import { Button, Modal } from './components';
import type { Product } from './types';
import { publishProductImage } from './product-image-sync';
import { ImageCompressionError, MAX_IMAGE_UPLOAD_BYTES, prepareImageForUpload } from './image-compression';

export function ProductImageEditor({ product, onClose, onSaved }: {
  product: Product;
  onClose: () => void;
  onSaved: (deleted: boolean) => void;
}) {
  const [pending, setPending] = useState<'compress' | 'upload' | 'delete' | null>(null);
  const [error, setError] = useState('');
  const busy = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function save(file?: File) {
    if (busy.current) return;
    busy.current = true;
    setPending(file ? file.size > MAX_IMAGE_UPLOAD_BYTES ? 'compress' : 'upload' : 'delete');
    setError('');
    try {
      const preparedFile = file ? await prepareImageForUpload(file) : undefined;
      const form = preparedFile ? new FormData() : undefined;
      if (preparedFile) { form!.append('file', preparedFile); setPending('upload'); }
      const result = await request<{ imageUrl: string | null }>(`/api/v1/admin/products/${product.id}/image`, {
        method: file ? 'POST' : 'DELETE', ...(form ? { body: form } : {}),
      });
      publishProductImage({ productId: product.id, imageUrl: result.imageUrl });
      onSaved(!file);
    } catch (caught) {
      setError(caught instanceof ApiError || caught instanceof ImageCompressionError ? caught.message : '主图更新失败，请重试');
    } finally {
      busy.current = false;
      setPending(null);
    }
  }

  return <Modal title="主图" description={`${product.code} · ${product.name}。此商品的所有规格共用这张主图。`}
    onClose={() => { if (!busy.current) onClose(); }}
    footer={<>
      <Button variant="secondary" disabled={pending !== null} onClick={onClose}>取消</Button>
      {product.imageUrl && <Button variant="danger" disabled={pending !== null} onClick={() => void save()}>
        <Trash />{pending === 'delete' ? '删除中…' : '删除主图'}
      </Button>}
      <Button disabled={pending !== null} onClick={() => inputRef.current?.click()}>
        <UploadSimple />{pending === 'compress' ? '压缩中…' : pending === 'upload' ? '上传中…' : product.imageUrl ? '修改主图' : '上传主图'}
      </Button>
    </>}>
    <div className="product-image-editor">
      {product.imageUrl ? <img src={product.imageUrl} alt={`${product.name}主图`} /> : <p>暂无主图，请上传图片。</p>}
      <p>支持 JPG、PNG、WebP。超过 200 KB 自动压缩至 200 KB 以下后上传。</p>
      <input ref={inputRef} type="file" hidden aria-label="选择商品主图"
        accept="image/jpeg,image/png,image/webp" disabled={pending !== null}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = '';
          if (file) void save(file);
        }} />
      {error && <p className="form-error" role="alert">{error}</p>}
    </div>
  </Modal>;
}
